import { cleanBalances, confirmedDrop, MAX_BALLS, newRequestId, pendingKey, validPending, validSettings, validateConfig } from './plinkoModel';
const fields = ['bet', 'rows', 'risk', 'currency'];
const reference = value => Object.fromEntries(['request_id', ...fields].map(key => [key, value[key]]));
const message = error => typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : error?.message || 'Connection interrupted. Resume the same drop.';

// The map is a presentation queue, never a wallet or outcome authority. POSTs
// serialize through the existing one-receipt settlement lifecycle, while up to
// five confirmed paths can animate together. Persist ONLY request references.
export class PlinkoController {
  constructor({ owner, api, storage, onChange, onBalances, onLand, onRejected }) {
    Object.assign(this, { owner, api, storage, onChange, onBalances, onLand, onRejected });
    this.key = pendingKey(owner); this.live = true; this.abort = new AbortController(); this.pending = new Map(); this.history = []; this.config = null; this.loading = true; this.processing = false; this.error = ''; this.syncError = ''; this.historyError = false; this.corrupt = false; this.result = null; this.latestBalances = null;
  }
  snapshot() { return { owner: this.owner, config: this.config, loading: this.loading, busy: this.processing, error: this.error, syncError: this.syncError, historyError: this.historyError, result: this.result, history: [...this.history], drops: [...this.pending.values()].map(item => ({ ...item })) }; }
  publish() { if (this.live) this.onChange?.(this.snapshot()); }
  async get(path) { const response = await this.api.get('/casino/plinko' + path, { signal: this.abort.signal }); return response.data; }
  save(required = false) {
    try { if (this.pending.size) this.storage.setItem(this.key, JSON.stringify([...this.pending.values()].map(item => reference(item.request)))); else this.storage.removeItem(this.key); }
    catch (error) { if (required) throw new Error('Recovery storage is unavailable. Enable session storage before placing a drop.'); }
  }
  balances(value) { if (value) this.latestBalances = cleanBalances(value); }
  applyBalances() { if (this.live && this.latestBalances) { try { Promise.resolve(this.onBalances?.(this.latestBalances)).catch(() => {}); } catch { /* refreshing cannot replay a wager */ } } }
  addHistory(round) { this.history = [round, ...this.history.filter(item => item.request_id !== round.request_id)].slice(0, 30); }
  async loadHistory() {
    try {
      const data = await this.get('/history'); if (!this.live) return;
      if (!Array.isArray(data) || data.length > 30) throw new Error('Invalid history response');
      const rows = data.map(row => confirmedDrop(row)); this.history = rows; if (this.result) this.addHistory(this.result); this.historyError = false;
    } catch { if (this.live) this.historyError = true; }
    this.publish();
  }
  async connect() {
    if (!this.live || this.processing || [...this.pending.values()].some(item => item.status === 'ready')) return;
    this.loading = true; this.error = ''; this.publish();
    try {
      if (!this.pending.size && !this.corrupt) {
        const raw = this.storage.getItem(this.key);
        if (raw != null) {
          const saved = JSON.parse(raw);
          if (!Array.isArray(saved) || saved.length > MAX_BALLS || saved.some(item => !validPending(item)) || new Set(saved.map(item => item.request_id)).size !== saved.length || saved.some(item => fields.some(field => item[field] !== saved[0][field]))) throw new Error('Saved drop references could not be read. Contact support before placing another drop.');
          for (const request of saved) this.pending.set(request.request_id, { request: Object.freeze({ ...request }), status: 'unconfirmed', submitted: true });
        }
      }
    } catch (error) { this.corrupt = true; this.syncError = message(error); }
    const responses = await Promise.allSettled([this.get('/config'), this.get('/active')]);
    if (!this.live) return;
    try { if (responses[0].status !== 'fulfilled') throw responses[0].reason; this.config = validateConfig(responses[0].value); }
    catch (error) { this.config = null; this.error = message(error); }
    let walletConfirmed = false;
    try {
      if (responses[1].status !== 'fulfilled') throw responses[1].reason;
      const active = responses[1].value;
      if (active.round) { const round = confirmedDrop(active.round); this.result = round; this.addHistory(round); this.balances(cleanBalances(round.balances)); }
      else this.balances(cleanBalances(active.balances));
      walletConfirmed = true;
    } catch (error) { this.syncError = 'Could not confirm your wallet. Reconnect before placing another drop.'; }
    for (const [id, item] of [...this.pending]) {
      try {
        const data = await this.get('/round/' + encodeURIComponent(id)); if (!this.live) return;
        if (data.request_id !== id) throw new Error('Wrong drop reference');
        if (data.status === 'completed') { const round = confirmedDrop(data, item.request); this.result = round; this.addHistory(round); this.balances(round.balances); this.pending.delete(id); }
        else if (data.status === 'not_placed' && data.payout == null && data.path == null) { this.pending.delete(id); this.error = 'Your previous drop was not placed. It did not charge your wallet.'; }
        else item.status = 'unconfirmed';
      } catch { item.status = 'unconfirmed'; }
    }
    if (!this.corrupt && this.pending.size) this.syncError = 'A drop is awaiting confirmation. Resume the same drop before playing again.';
    else if (!this.corrupt && walletConfirmed) this.syncError = '';
    if (!this.corrupt) this.save(); await this.loadHistory(); if (!this.live) return;
    this.loading = false; this.applyBalances(); this.publish();
  }
  launch(settings) {
    if (!this.live || this.loading || !this.config?.enabled || this.syncError || this.corrupt || this.pending.size >= MAX_BALLS || !validSettings(settings)) return null;
    if ([...this.pending.values()].some(item => fields.some(field => item.request[field] !== settings[field]))) return null;
    let request;
    try { request = Object.freeze({ request_id: newRequestId(), ...Object.fromEntries(fields.map(field => [field, settings[field]])) }); }
    catch { this.error = 'Secure request references are unavailable in this browser.'; this.publish(); return null; }
    this.pending.set(request.request_id, { request, status: 'queued', submitted: false });
    try { this.save(true); } catch (error) { this.pending.delete(request.request_id); this.error = message(error); this.publish(); return null; }
    this.error = ''; this.publish(); void this.pump(); return request;
  }
  ready(data, item) {
    const round = confirmedDrop(data, item.request); this.balances(round.balances); item.round = round; item.status = 'ready'; this.error = ''; this.syncError = ''; this.publish(); return round;
  }
  reject(item, text) {
    this.pending.delete(item.request.request_id); this.save(); this.error = text;
    if (this.live) { try { this.onRejected?.(item.request); } catch { /* presentation cannot replay a wager */ } } if (!this.pending.size) this.applyBalances(); this.publish();
  }
  async reconcile(item) {
    const data = await this.get('/round/' + encodeURIComponent(item.request.request_id)); if (!this.live) return false;
    if (data.request_id !== item.request.request_id) throw new Error('Wrong drop reference');
    if (data.status === 'completed') { this.ready(data, item); return true; }
    if (data.status === 'not_placed' && data.payout == null && data.path == null) { this.reject(item, 'This drop was not placed. Your wallet was not charged.'); return true; }
    return false;
  }
  async pump() {
    if (!this.live || this.processing || this.syncError) return;
    this.processing = true; this.publish();
    try {
      while (this.live && !this.syncError) {
        const item = [...this.pending.values()].find(value => value.status === 'queued'); if (!item) break;
        item.status = 'confirming'; this.publish();
        try {
          if (item.resuming) { try { if (await this.reconcile(item)) continue; } catch { /* explicit resume reuses the same reference */ } }
          if (!this.live) return;
          item.submitted = true;
          const { data } = await this.api.post('/casino/plinko', item.request, { signal: this.abort.signal, timeout: 15000 });
          if (!this.live) return; this.ready(data, item);
        } catch (error) {
          if (!this.live) return;
          try { if (await this.reconcile(item)) continue; } catch { /* an unknown read is not proof that a POST was unpaid */ }
          if (!this.live) return;
          item.status = 'unconfirmed'; this.syncError = 'Your drop is unconfirmed. Resume the same drop; it will not charge twice.'; this.error = message(error);
          for (const queued of [...this.pending.values()]) if (queued.status === 'queued' && !queued.submitted) this.reject(queued, this.error);
        }
      }
    } finally { this.processing = false; this.publish(); }
  }
  land(id) {
    const item = this.pending.get(id); if (!this.live || item?.status !== 'ready') return null;
    this.pending.delete(id); this.save(); this.result = item.round; this.addHistory(item.round);
    try { this.onLand?.(item.round); } catch { /* presentation cannot replay a paid drop */ } if (!this.pending.size) this.applyBalances(); this.publish(); return item.round;
  }
  resume() {
    if (!this.live || this.processing || this.loading || this.corrupt || ![...this.pending.values()].some(item => item.status === 'unconfirmed')) return;
    this.syncError = ''; this.error = '';
    for (const item of this.pending.values()) if (item.status === 'unconfirmed') { item.status = 'queued'; item.resuming = true; }
    this.publish(); void this.pump();
  }
  dispose() { this.live = false; this.abort.abort(); }
}
